/**
 * VoiceVisualPanel — eingeblendetes Visual im Voice-Screen (#1399, Voice-Visuals).
 *
 * Rendert das vom Gateway gepushte visual.show-Payload {typ, meta, data}:
 *   anwesenheit   Tabelle Name/Status/Seit/Letzte Buchung
 *   urlaube       Tabelle Name/Grund/Bis
 *   versand_kpis  KPI-Karten + Recharts-BarChart (Ist vs. Soll je Tag)
 *   umsatz        KPI-Karten + Recharts-BarChart (Umsatz je Tag)
 *
 * Design gem. SKILL-design.md: Border-Cards ohne Shadow, KPI-Werte
 * --text-primary, Charts Blau #338edc / Cyan #22D3EE / Indigo #6366F1,
 * Tabellen-Header --bg-inset. Reine Anzeige (read-only).
 *
 * #1391 — `dark`-Prop: Kiosk-Bühne /flow (dunkle FlowAI-Palette aus Manuels
 * demo.html-Vorlage, NICHT das App-Theme): Ink #070B14/#0B1220, Linien
 * rgba(96,212,255,.10), Text #E8EEF6, Muted #5B6B82, Charts Teal #27B2BD /
 * Mint #38EBBA / Sky #60D4FF. KPI-Werte bleiben HELL (#E8EEF6), nie farbig.
 *
 * @since v2.645.0 (#1399)
 */
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { useLanguage } from '../../context/LanguageContext';

const STATUS_COLORS = {
  anwesend: { color: '#10B981', bg: 'rgba(16,185,129,0.10)' },
  pause: { color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
  gegangen: { color: '#6B7280', bg: 'rgba(107,114,128,0.10)' },
};

// Dunkle Status-Badges: gleiche Semantik, an Mint/Sky/Muted angelehnt.
const STATUS_COLORS_DARK = {
  anwesend: { color: '#38EBBA', bg: 'rgba(56,235,186,0.12)' },
  pause: { color: '#60D4FF', bg: 'rgba(96,212,255,0.12)' },
  gegangen: { color: '#5B6B82', bg: 'rgba(91,107,130,0.15)' },
};

// Palette: light = App-Theme (CSS-Variablen), dark = FlowAI-Bühne (#1391).
function getPalette(dark) {
  if (!dark) {
    return {
      cardBorder: 'var(--border-card, #E5E7EB)',
      tableBorder: 'var(--border-table, #F3F4F6)',
      headBg: 'var(--bg-inset, #F8F9FC)',
      textPrimary: 'var(--text-primary)',
      textSecondary: 'var(--text-secondary)',
      textTertiary: 'var(--text-tertiary)',
      grid: '#E5E7EB',
      axis: '#6B7280',
      barA: '#338edc',
      barB: '#22D3EE',
      barNeutral: '#E5E7EB',
      status: STATUS_COLORS,
      tooltip: undefined,
    };
  }
  return {
    cardBorder: 'rgba(96,212,255,0.10)',
    tableBorder: 'rgba(96,212,255,0.08)',
    headBg: '#0B1220',
    textPrimary: '#E8EEF6',
    textSecondary: '#A9B7C9',
    textTertiary: '#5B6B82',
    grid: 'rgba(96,212,255,0.10)',
    axis: '#5B6B82',
    barA: '#27B2BD',
    barB: '#38EBBA',
    barNeutral: 'rgba(91,107,130,0.35)',
    status: STATUS_COLORS_DARK,
    tooltip: { backgroundColor: '#0B1220', border: '1px solid rgba(96,212,255,0.15)', borderRadius: 8, color: '#E8EEF6' },
  };
}

function fmtDate(v, lang) {
  if (!v) return '–';
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString(lang === 'de' ? 'de-AT' : 'en-GB', { day: '2-digit', month: '2-digit' });
}

function fmtTime(v) {
  if (!v) return '–';
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Vienna' });
}

function fmtEur(v) {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
}

function fmtInt(v) {
  return new Intl.NumberFormat('de-AT').format(Math.round(v || 0));
}

function Kpi({ label, value, pal }) {
  return (
    <div className="rounded-[10px] px-4 py-3 flex-1 min-w-[120px]" style={{ border: `1px solid ${pal.cardBorder}` }}>
      <div className="text-[11px] uppercase tracking-[0.6px]" style={{ color: pal.textTertiary }}>{label}</div>
      {/* KPI-Werte IMMER textPrimary (hell), NIE farbig (SKILL-design.md / #1391) */}
      <div className="mt-1 text-xl font-semibold" style={{ color: pal.textPrimary }}>{value}</div>
    </div>
  );
}

function TableShell({ headers, children, pal }) {
  return (
    <div className="overflow-y-auto max-h-[420px] rounded-[10px]" style={{ border: `1px solid ${pal.cardBorder}` }}>
      <table className="w-full text-sm">
        <thead className="sticky top-0" style={{ background: pal.headBg }}>
          <tr>
            {headers.map((h) => (
              <th key={h} className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.6px]" style={{ color: pal.textSecondary }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function AnwesenheitTable({ data, t, lang, pal }) {
  const rows = data.rows || [];
  if (!rows.length) return <div className="text-sm" style={{ color: pal.textTertiary }}>{t('voice.visual.empty')}</div>;
  return (
    <TableShell pal={pal} headers={[t('voice.visual.col.name'), t('voice.visual.col.status'), t('voice.visual.col.seit'), t('voice.visual.col.letzteBuchung')]}>
      {rows.map((r, i) => {
        const sc = pal.status[r.status] || { color: pal.textTertiary, bg: pal.headBg };
        return (
          <tr key={`${r.name}-${i}`} style={{ borderTop: `1px solid ${pal.tableBorder}` }}>
            <td className="px-3 py-2" style={{ color: pal.textPrimary }}>{r.name}</td>
            <td className="px-3 py-2">
              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: sc.color, background: sc.bg }}>
                {t(`voice.visual.status.${r.status}`) !== `voice.visual.status.${r.status}` ? t(`voice.visual.status.${r.status}`) : r.status}
              </span>
            </td>
            <td className="px-3 py-2" style={{ color: pal.textSecondary }}>{fmtTime(r.seit)}</td>
            <td className="px-3 py-2" style={{ color: pal.textSecondary }}>{fmtTime(r.letzte_buchung)}</td>
          </tr>
        );
      })}
    </TableShell>
  );
}

function UrlaubeTable({ data, t, lang, pal }) {
  const rows = data.rows || [];
  if (!rows.length) return <div className="text-sm" style={{ color: pal.textTertiary }}>{t('voice.visual.empty')}</div>;
  return (
    <TableShell pal={pal} headers={[t('voice.visual.col.name'), t('voice.visual.col.grund'), t('voice.visual.col.bis')]}>
      {rows.map((r, i) => (
        <tr key={`${r.name}-${i}`} style={{ borderTop: `1px solid ${pal.tableBorder}` }}>
          <td className="px-3 py-2" style={{ color: pal.textPrimary }}>{r.name}</td>
          <td className="px-3 py-2" style={{ color: pal.textSecondary }}>{r.grund || '–'}</td>
          <td className="px-3 py-2" style={{ color: pal.textSecondary }}>{fmtDate(r.bis, lang)}</td>
        </tr>
      ))}
    </TableShell>
  );
}

function VersandChart({ data, t, lang, pal }) {
  const series = (data.series || []).map((s) => ({ ...s, label: fmtDate(s.datum, lang) }));
  const k = data.kpis || {};
  return (
    <div>
      <div className="flex flex-wrap gap-3">
        <Kpi pal={pal} label={t('voice.visual.kpi.istGesamt')} value={fmtInt(k.ist_gesamt)} />
        <Kpi pal={pal} label={t('voice.visual.kpi.sollGesamt')} value={fmtInt(k.soll_gesamt)} />
        <Kpi pal={pal} label={t('voice.visual.kpi.verspaetet')} value={fmtInt(k.verspaetet_gesamt)} />
      </div>
      {series.length > 0 && (
        <div className="mt-4" style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={pal.grid} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: pal.axis }} tickLine={false} axisLine={{ stroke: pal.grid }} />
              <YAxis tick={{ fontSize: 11, fill: pal.axis }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={pal.tooltip} />
              <Legend wrapperStyle={{ fontSize: 12, color: pal.textSecondary }} />
              <Bar dataKey="soll" name={t('voice.visual.serie.soll')} fill={pal.barNeutral} radius={[3, 3, 0, 0]} />
              <Bar dataKey="ist" name={t('voice.visual.serie.ist')} fill={pal.barA} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {!series.length && <div className="mt-4 text-sm" style={{ color: pal.textTertiary }}>{t('voice.visual.empty')}</div>}
    </div>
  );
}

function UmsatzChart({ data, t, lang, pal }) {
  const series = (data.series || []).map((s) => ({ ...s, label: fmtDate(s.datum, lang) }));
  const k = data.kpis || {};
  return (
    <div>
      <div className="flex flex-wrap gap-3">
        <Kpi pal={pal} label={t('voice.visual.kpi.umsatz')} value={fmtEur(k.umsatz_gesamt)} />
        <Kpi pal={pal} label={t('voice.visual.kpi.db')} value={fmtEur(k.db_gesamt)} />
      </div>
      {series.length > 0 && (
        <div className="mt-4" style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={pal.grid} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: pal.axis }} tickLine={false} axisLine={{ stroke: pal.grid }} />
              <YAxis tick={{ fontSize: 11, fill: pal.axis }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => fmtEur(v)} contentStyle={pal.tooltip} />
              <Legend wrapperStyle={{ fontSize: 12, color: pal.textSecondary }} />
              <Bar dataKey="umsatz" name={t('voice.visual.serie.umsatz')} fill={pal.barA} radius={[3, 3, 0, 0]} />
              <Bar dataKey="db" name={t('voice.visual.serie.db')} fill={pal.barB} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {!series.length && <div className="mt-4 text-sm" style={{ color: pal.textTertiary }}>{t('voice.visual.empty')}</div>}
    </div>
  );
}

export default function VoiceVisualPanel({ visual, dark = false }) {
  const { t, language } = useLanguage();
  if (!visual || !visual.typ) return null;
  const { typ, meta = {}, data = {} } = visual;
  const pal = getPalette(dark);

  const subtitle = meta.datum
    ? fmtDate(meta.datum, language)
    : meta.days
      ? t('voice.visual.lastDays').replace('{days}', meta.days)
      : '';

  return (
    <div className="w-full min-w-0">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-sm font-semibold" style={{ color: pal.textPrimary }}>{t(`voice.visual.title.${typ}`)}</div>
        {subtitle && <div className="text-xs" style={{ color: pal.textTertiary }}>{subtitle}</div>}
      </div>
      {typ === 'anwesenheit' && <AnwesenheitTable data={data} t={t} lang={language} pal={pal} />}
      {typ === 'urlaube' && <UrlaubeTable data={data} t={t} lang={language} pal={pal} />}
      {typ === 'versand_kpis' && <VersandChart data={data} t={t} lang={language} pal={pal} />}
      {typ === 'umsatz' && <UmsatzChart data={data} t={t} lang={language} pal={pal} />}
    </div>
  );
}
